import { useTheme } from 'next-themes';
import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';

import { cn } from '@/lib/utils';

const options: { icon: ComponentType<{ className?: string }>; label: string; value: string }[] = [
  { icon: Sun, label: 'Light', value: 'light' },
  { icon: Moon, label: 'Dark', value: 'dark' },
  { icon: Monitor, label: 'System', value: 'system' },
];

/** The full three-option control. Settings only — see the note on the compact one. */
export function AppearanceToggle({ className }: { className?: string }) {
  const { theme, mounted, setTheme } = useMountedTheme();

  return (
    <div className={cn('inline-flex items-center gap-1 rounded-full border border-[rgb(var(--fgc)/0.14)] p-[5px]', className)}>
      {options.map(({ label, value, icon: Icon }) => {
        const isActive = mounted && theme === value;

        return (
          <button
            key={value}
            type="button"
            aria-pressed={isActive}
            onClick={() => setTheme(value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
              isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
            )}>
            <Icon className="size-4" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Compact light/dark toggle for the persistent top bar.
 *
 * The three-option control above reads fine on a settings page but is far too
 * wide — labels and all — for a 56px header shared by every page, mobile
 * included. This drops `system` and renders as a single icon button matching
 * the header's other icon buttons, so the full control stays the only place
 * `system` can be selected.
 *
 * `system` still reads correctly here: the button reflects and toggles the
 * *effective* theme, it just cannot be chosen from this control.
 */
export function CompactAppearanceToggle({ className }: { className?: string }) {
  const { isDark, setTheme } = useMountedTheme();

  return (
    <button
      type="button"
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className={cn(
        'flex size-8 items-center justify-center rounded-[10px] border border-[rgb(var(--fgc)/0.14)] bg-[rgb(var(--fgc)/0.04)] text-foreground transition-colors duration-200 hover:border-primary hover:bg-transparent hover:text-primary',
        className,
      )}>
      {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
    </button>
  );
}

/**
 * next-themes resolves the effective theme only after mount, so `resolvedTheme`
 * is undefined on the first render. Rendering a state that depends on it before
 * then would show the wrong icon for a frame. Every consumer here waits.
 */
function useMountedTheme() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const { theme, setTheme, resolvedTheme } = useTheme();
  return { theme, mounted, setTheme, isDark: mounted && resolvedTheme === 'dark' };
}
